import { AutogenLayoutCustomerServiceProfileFilter } from "./autogen/AutogenLayoutCustomerServiceProfileFilter";
import { CompanySettings, DatePart, DateUtil, LogManager, ModelRow, StringUtil } from "@mcleod/core";
import { RowCsmProfileFields } from "../../../planning/csm/src/models/autogen/AutogenModelCsmProfile";

const log = LogManager.getLogger("lme/dispatch/planning/csm/CustomerServiceProfileFilter");

export class CustomerServiceProfileFilter extends AutogenLayoutCustomerServiceProfileFilter {
    onLoad(): void | Promise<void> {
        this.layoutProfile.textboxDescr.enabled = false;
    }
}

interface IncludeExcludeList {
    isExclude: boolean;
    dataField: string | string[];
    values: string[];
}

export class CustomerServiceProfileFilterer {
    private profileRow: ModelRow;
    private includeLists: IncludeExcludeList[] = [];
    private readonly orderStartDate: Date;
    private readonly orderEndDate: Date;

    constructor(row: ModelRow) {
        this.profileRow = row;
        this.addIncludeExcludeList("revenue_code", "revenue_code_id");
        this.addIncludeExcludeList("order_type_id", "order_type_id", "exclude_order_type");
        this.addIncludeExcludeList("commodity", "commodity_id");
        this.addIncludeExcludeList("order_customers", "customer_id", "exclude_customer");
        this.addIncludeExcludeList("operations_user", "operations_user_id");
        this.addIncludeExcludeList("brokerage_status");
        this.addIncludeExcludeList("entry_user", "entered_user_id");
        this.addIncludeExcludeList("order_status");
        this.addIncludeExcludeList("move_status");
        this.addIncludeExcludeList("osd_status");
        this.addIncludeExcludeList("appt_status", ["origin_appt_status", "destination_appt_status"]);
        this.addIncludeExcludeList("auth_status", ["origin_auth_status", "destination_auth_status"]);
        if (!this.profileRow.isNull("start_day"))
            this.orderStartDate = DateUtil.parseDateWithKeywords(this.profileRow.get("start_day"), true, false);
        if (!this.profileRow.isNull("orders_days")) {
            this.orderEndDate = DateUtil.dateAdd(DatePart.DAY, this.orderStartDate ?? new Date(), parseInt(this.profileRow.get("orders_days")));
        }
    }

    dataPasses(data: ModelRow): boolean {
        log.debug("Checking data", data);

        const currentCompanyId = CompanySettings.get()?.id;
        if (currentCompanyId && data.get("company_id") !== currentCompanyId) {
            return false;
        }

        if (["V"].includes(data.get("move_status")) || !["L"].includes(data.get("move_loaded")) || !StringUtil.isEmptyString(data.get("transfer_to_billing_date"))) {
            return false;
        }
        for (const list of this.includeLists) {
            let listMatches = false;
            if (list.dataField instanceof Array) {
                for (const field of list.dataField) {
                    listMatches = this.findItem(list.values, data.get(field));
                    if (listMatches)
                        break;
                }
            }
            else {
                listMatches = this.findItem(list.values, data.get(list.dataField));
            }
            if ((list.isExclude && listMatches) || (!list.isExclude && !listMatches)) {
                return false;
            }
        }
        if (!this.preassignedMatches(data))
            return false;
        if (!this.ltlMatches(data))
            return false;
        if (!this.lateOnlyMatches(data))
            return false;
        if (!this.missingAppointmentsMatches(data))
            return false;
        if (!this.serviceIncidentMatches(data))
            return false;
        if (!this.subjectLoadMatches(data))
            return false;
        if (!this.onHoldMatches(data))
            return false;
        if (!this.readyToBillMatches(data))
            return false;
        if (!this.inboundOutboundMatches(data))
            return false;
        if (!this.dateTimeMatches(data))
            return false;
        if (!this.apptStatusMatches(data))
            return false;
        if (!this.authStatusMatches(data))
            return false;
        if (!this.movementTypeMatches(data))
            return false;
        return true;
    }

    private preassignedMatches(data: ModelRow): boolean {
        if (this.profileRow.getBoolean("preassigned_order", false))
            return true;
        const isBrokerage = data.getBoolean("brokerage");
        const hasCarrier = isBrokerage && !data.isNull("override_payee_id");
        const hasDriverAndTractor = !isBrokerage && !data.isNull("tractor_number") && !data.isNull("driver1_id");
        return !hasCarrier && !hasDriverAndTractor;
    }

    private ltlMatches(data: ModelRow): boolean {
        return this.profileRow.getBoolean("show_ltl_orders", false) || !data.getBoolean("ltl");
    }

    private lateOnlyMatches(data: ModelRow): boolean {
        return !this.profileRow.getBoolean("order_late_only", false) || data.getBoolean("origin_eta_late");
    }

    private missingAppointmentsMatches(data: ModelRow): boolean {
        return this.profileRow.getBoolean("missing_appts_only", false) || !data.getBoolean("has_missing_appointments");
    }

    private serviceIncidentMatches(data: ModelRow): boolean {
        return this.profileRow.getBoolean("service_incident_only", false) || !data.getBoolean("has_servicefails");
    }

    private subjectLoadMatches(data: ModelRow): boolean {
        return this.profileRow.getBoolean("subject_loads", false) || !data.get("order_id").startsWith("S");
    }

    private onHoldMatches(data: ModelRow): boolean {
        return this.profileRow.getBoolean("include_on_hold_orders", false) || !data.getBoolean("hold", false);
    }

    private readyToBillMatches(data: ModelRow): boolean {
        if (this.profileRow.getBoolean("display_until_ready_to_bill", false))
            return true;
        return !data.getBoolean("ready_to_bill", false) || !["D"].includes(data.get("move_status"));
    }

    private movementTypeMatches(data: ModelRow<any>): boolean {
        const moveTypeFilters = this.tokenizeField("movement_type");
        if (moveTypeFilters.length === 0)
            return true;
        const isExclude = this.profileRow.getBoolean("exclude_movement_type", false);

        if (moveTypeFilters.length > 1 && isExclude) {
            return false;
        }
        if (moveTypeFilters.length > 1 && !isExclude) {
            return true;
        }
        
        const moveType = moveTypeFilters[0];
        const brokerage = data.get("brokerage", false);
        if (moveType === "B") {
            return isExclude ? !brokerage : brokerage;
        } else if (moveType === "A") {
            return isExclude ? brokerage : !brokerage;
        }
    }

    apptStatusMatches(data: ModelRow<any>): boolean {
        const apptStatusFilters = this.tokenizeField("appt_status");
        if (apptStatusFilters.length === 0)
            return true;
        const apptStatusData = data.get("stop_appt_statuses", null);
        let matches = apptStatusData?.some(status => this.findItem(apptStatusFilters, status)) ?? false;
        if (this.profileRow.getBoolean("exclude_appt_status", false)) {
            matches = !matches;
        }
        return matches;
    }

    authStatusMatches(data: ModelRow<any>): boolean {
        const authStatusFilters = this.tokenizeField("auth_status");
        if (authStatusFilters.length === 0)
            return true;
        const authStatusData = data.get("stop_auth_statuses", null);
        let matches = authStatusData?.some(status => this.findItem(authStatusFilters, status)) ?? false;
        if (this.profileRow.getBoolean("exclude_auth_status", false)) {
            matches = !matches;
        }
        return matches;
    }

    private dateTimeMatches(data: ModelRow) {
        let filterDate: Date;
        if (!this.profileRow.isNull("order_ib_list") && "*" !== this.profileRow.get("order_ib_list") &&
            (this.profileRow.isNull("order_ob_list") || "*" === this.profileRow.get("order_ob_list")))
            filterDate = data.get("destination_early");
        else
            filterDate = data.get("origin_early");
        if (filterDate == null)
            return false;
        if (typeof filterDate === "string") // bummer - was hoping this would have already been a date in the ModelRow
            filterDate = DateUtil.parseDate(filterDate);
        filterDate = DateUtil.justDate(filterDate);
        return ((this.orderStartDate == null || this.orderStartDate <= filterDate) && this.orderEndDate >= filterDate);
    }

    private addIncludeExcludeList(includeExcludeListFieldName: keyof RowCsmProfileFields, dataFieldName?: string | string[], includeExcludeFlagFieldName?: string) {
        this.includeLists.push({
            isExclude: this.profileRow.getBoolean(includeExcludeFlagFieldName || "exclude_" + includeExcludeListFieldName.toString(), false),
            values: this.tokenizeField(includeExcludeListFieldName, ","),
            dataField: dataFieldName || includeExcludeListFieldName.toString()
        });
    }

    private tokenizeField(field: keyof RowCsmProfileFields, delimiter: string = ","): string[] {
        if (this.profileRow.isNull(field))
            return [];
        const result = this.profileRow.get(field).split(delimiter);
        for (let i = 0; i < result.length; i++)
            result[i] = result[i].trim();
        return result;
    }

    private inboundOutboundMatches(data: ModelRow): boolean {
        const orderIBList = this.tokenizeField("order_ib_list");
        const orderOBList = this.tokenizeField("order_ob_list");
        let inbound = this.findItem(orderIBList, this.getFilterValue(data, "ib", "destination"));
        let outbound = this.findItem(orderOBList, this.getFilterValue(data, "ob", "origin"));
        if (this.profileRow.getBoolean("exclude_inbound", false))
            inbound = !inbound;
        if (this.profileRow.getBoolean("exclude_outbound", false))
            outbound = !outbound;
        return inbound && outbound;
    }

    private getFilterValue(data: ModelRow, inboundOutboundPart: "ib" | "ob", origDestPart: "origin" | "destination"): string {
        const type = this.profileRow.get(`order_${inboundOutboundPart}_type`);
        switch (type) {
            case "Z": return data.get(origDestPart + "_zone");
            case "S": return data.get(origDestPart + "_state");
            case "C": return data.get(origDestPart + "zip");
            case "L": return data.get(origDestPart + "_location_id");
        }
        return null;
    }

    protected findItem(itemList: string[], item: string): boolean {
        if (itemList == null || itemList.length === 0 || "*" === itemList[0])
            return true;
        if (StringUtil.isEmptyString(item))
            return false;
        return itemList.indexOf(item) >= 0;
    }
}
